import { reportTtEvent } from '@/api/public';
import { getStoreState } from '@/store';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { getCookie } from '@/utils/tools';
import { sha256 } from 'js-sha256';
import { omit } from 'lodash-es';
import { Provider, ProviderType, TrackingEvent } from '../types';

interface TiktokConfig {
  pixelId: string;
  isEnabled?: boolean;
}

export class TiktokProvider implements Provider {
  type = ProviderType.TIKTOK;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: TiktokConfig;

  constructor(config: TiktokConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // Define ttq on window
      const w = window as any;
      const t = 'ttq';
      w.TiktokAnalyticsObject = t;

      const ttq = (w[t] = w[t] || []);

      // Setup methods
      const methods = [
        'page',
        'track',
        'identify',
        'instances',
        'debug',
        'on',
        'off',
        'once',
        'ready',
        'alias',
        'group',
        'enableCookie',
        'disableCookie',
      ];
      ttq.setAndDefer = function (t2: any, e: string) {
        t2[e] = function () {
          // eslint-disable-next-line prefer-rest-params
          t2.push([e].concat(Array.prototype.slice.call(arguments, 0)));
        };
      };

      for (let i = 0; i < methods.length; i++) {
        ttq.setAndDefer(ttq, methods[i]);
      }

      // Load the script
      return new Promise((resolve) => {
        // Save reference to this for use in closures
        const self = this;

        ttq.instance = function (t1: string) {
          const e = ttq._i[t1] || [];
          for (let n = 0; n < methods.length; n++) {
            ttq.setAndDefer(e, methods[n]);
          }
          return e;
        };

        ttq.load = function (e: string, n: any) {
          const i = 'https://analytics.tiktok.com/i18n/pixel/events.js';
          ttq._i = ttq._i || {};
          ttq._i[e] = [];
          ttq._i[e]._u = i;
          ttq._t = ttq._t || {};
          ttq._t[e] = +new Date();
          ttq._o = ttq._o || {};
          ttq._o[e] = n || {};

          const o = document.createElement('script');
          o.type = 'text/javascript';
          o.async = true;
          o.src = `${i}?sdkid=${e}&lib=${t}`;
          o.onload = () => {
            self.initialized = true;
            resolve(true);
          };
          o.onerror = () => {
            resolve(false);
          };

          const a = document.getElementsByTagName('script')[0];
          a.parentNode?.insertBefore(o, a);
        };

        // Load the pixel
        ttq.load(this.config.pixelId);
        ttq.page();
      });
    } catch (error) {
      logger.error('Failed to initialize TikTok Pixel:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      if (this.loggingEnabled) {
        logger.log('TikTok tracking event:', event);
      }

      const { ttq } = window as any;
      // 获取当前的 store 状态
      const storeState = getStoreState();

      const userInfo = storeState.userInfo;
      ttq.identify({
        email: userInfo.email || '',
        phone_number:
          userInfo.tel_code && userInfo.telephone
            ? '+' + userInfo.tel_code + userInfo.telephone
            : '',
      });
      ttq.track(event.eventName, omit(event.properties, ['event_id', 'server_report_data']), {
        event_id: event.properties.event_id,
      });

      const serverReportData = event.properties.server_report_data;
      this.reportTtEventToBackend(
        event.eventName,
        event.properties.event_id,
        serverReportData.contents,
        serverReportData.currency,
        serverReportData.value,
        userInfo,
      );
    } catch (error) {
      logger.error('TikTok tracking error:', error);
    }
  }

  async reportTtEventToBackend(
    eventName,
    eventId,
    contents,
    currency,
    value,
    userInfo,
  ): Promise<void> {
    const d = new Date();
    d.setHours(d.getHours(), d.getMinutes() - d.getTimezoneOffset());
    const params = Object.assign(
      {},
      {
        contents: contents || null,
        currency: currency || null,
        value: value || null,
      },
    );
    const user = Object.assign(
      {},
      {
        ttclid: getCookie('ttclid') || null,
        ttp: getCookie('_ttp') || '',
        user_agent: navigator.userAgent,
        external_id: userInfo.id ? `${userInfo.id}` : null,
        phone: userInfo.telephone ? sha256(userInfo.telephone) : null,
        email: userInfo.email ? sha256(userInfo.email) : null,
      },
    );
    for (const key in params) {
      if (!params[key]) {
        delete params[key];
      }
    }
    for (const key in user) {
      if (!user[key]) {
        delete user[key];
      }
    }
    reportTtEvent({
      event: eventName,
      event_id: `${eventId}` || '',
      event_time: Math.trunc(new Date().valueOf() / 1000),
      user,
      properties: params,
    });
  }

  /**
   * 启用TikTok日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用TikTok日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
